import { BaseTool } from '../core/BaseTool'
import { readData } from '@utils/DataService'
import { log } from '@reusables/logger'

/**
 * FirestoreTool - Core tool for querying Firestore database
 * Power Level: 2 (Moderate - can read any collection)
 */
export class FirestoreTool extends BaseTool {
	constructor() {
		super({
			name: 'firestore',
			description: 'Query and read data from Firestore collections',
			powerLevel: 2,
			schema: {
				collection: {
					type: 'string',
					required: true,
					description: 'Collection path (e.g., "users", "events")'
				},
				tenantId: {
					type: 'string',
					required: true,
					description: 'Tenant ID for multi-tenant support'
				},
				filter: {
					type: 'object',
					required: false,
					description: 'Query filters (where, orderBy, limit)'
				},
				documentId: {
					type: 'string',
					required: false,
					description: 'Specific document ID to fetch'
				}
			}
		})
	}

	/**
	 * Execute Firestore query
	 * @param {Object} params
	 * @param {string} params.collection - Collection name
	 * @param {string} params.tenantId - Tenant ID
	 * @param {Object} params.filter - Query filters
	 * @param {string} params.documentId - Optional document ID
	 * @param {Object} _context - Execution context
	 * @returns {Promise<Object>}
	 */
	async execute(params, _context) {
		const { collection, tenantId, filter, documentId } = params

		try {
			// Build the collection path
			const ref = documentId
				? `tenant/${tenantId}/${collection}/${documentId}`
				: `tenant/${tenantId}/${collection}`

			log(`FirestoreTool: Querying ${ref}`, { filter })

			// Execute query using DataService
			const results = await readData({ ref, filter })

			// Format response
			if (documentId) {
				return {
					success: true,
					data: results,
					count: 1,
					message: 'Document retrieved successfully'
				}
			}

			return {
				success: true,
				data: results,
				count: results.length,
				message: `Found ${results.length} result(s)`
			}
		} catch (error) {
			log('FirestoreTool error:', error)
			return {
				success: false,
				error: error.message,
				data: null
			}
		}
	}

	/**
	 * Helper: Query with where clause
	 * @param {string} collection - Collection name
	 * @param {string} tenantId - Tenant ID
	 * @param {Object} whereClause - Where conditions
	 * @param {Object} _context - Execution context
	 * @returns {Promise<Object>}
	 */
	async queryWhere(collection, tenantId, whereClause, _context) {
		return this.run(
			{
				collection,
				tenantId,
				filter: { where: whereClause }
			},
			_context
		)
	}

	/**
	 * Helper: Query with multiple conditions
	 * @param {string} collection - Collection name
	 * @param {string} tenantId - Tenant ID
	 * @param {Object} queryOptions - Full query options
	 * @param {Object} context - Execution context
	 * @returns {Promise<Object>}
	 */
	async query(collection, tenantId, queryOptions, context) {
		return this.run(
			{
				collection,
				tenantId,
				filter: queryOptions
			},
			context
		)
	}

	/**
	 * Helper: Get single document by ID
	 * @param {string} collection - Collection name
	 * @param {string} tenantId - Tenant ID
	 * @param {string} documentId - Document ID
	 * @param {Object} context - Execution context
	 * @returns {Promise<Object>}
	 */
	async getDocument(collection, tenantId, documentId, context) {
		return this.run(
			{
				collection,
				tenantId,
				documentId
			},
			context
		)
	}
}
